import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { ArrowUpTrayIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useParams } from 'react-router';
import { DateTime } from 'luxon';

import { SmallSpinner } from '../../../../common/Atoms/SmallSpinner';
import { Paragraph } from '../../../../common/Atoms/Typography/Paragraph';
import { Badge } from '../../../../common/Atoms/Badge';
import { classNames } from '../../../../common/lib/classNames';
import { useFetchInvoiceAttachmentsQuery, useUploadInvoicePdfMutation } from '../../../services/api/invoiceApi/invoice';
import { useInvoice } from '../../../hooks/useInvoice';
import { useAccountingSystemName } from '../../../hooks/useAccountingSystemName';
import { useSettings } from '../../../hooks/useSettings';
import { HelpIcon } from '../../Molecules/HelpIcon';

export function InvoiceAttachments() {
  const { organisationId, invoiceId } = useParams();
  const { emailedPdf } = useInvoice();
  const accountingSystemName = useAccountingSystemName();
  const [isExpanded, setIsExpanded] = useState(false);
  const shouldFetchAttachments = isExpanded && !!organisationId && !!invoiceId;
  const panelId = `invoice-attachments-panel`;
  const {
    currentData,
    isFetching,
    isError,
  } = useFetchInvoiceAttachmentsQuery({
    organisationId: organisationId || ``,
    invoiceId: invoiceId || ``,
  }, {
    skip: !shouldFetchAttachments,
  });
  const nominatedAlternativeAttachment = currentData?.attachments.find(attachment => attachment.nominatedAlternativePdf);
  const { data: settings } = useSettings();
  // Only useful while the org is sending an alternative PDF instead of the accounting system's
  const usingAlternativePdf = !!settings?.useAlternativeInvoicePdf;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadInvoicePdf, { isLoading: isUploading }] = useUploadInvoicePdfMutation();

  async function onFileSelected(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];

    // Clear it straight away so picking the same file twice still fires a change
    event.target.value = ``;

    if (!file || !organisationId || !invoiceId) {
      return;
    }

    try {
      await uploadInvoicePdf({ organisationId, invoiceId, file }).unwrap();
      toast.success(`PDF attached to this invoice`);
    }
    catch (err) {
      toast.error(err?.data || `Could not attach that PDF`);
    }
  }

  function onToggleExpanded() {
    const next = !isExpanded;
    setIsExpanded(next);
  }

  return (
    <div className={ `border-t border-gray-200 px-4 py-4 sm:px-6` }>
      { (emailedPdf || usingAlternativePdf) ? (
        <div className={ `mb-3 flex flex-wrap items-center gap-2` }>
          { emailedPdf ? (
            <>
              <Badge color={ `blue` }>
                <EnvelopeIcon className={ `mr-1 h-3.5 w-3.5` } />
                { `PDF emailed in` }
              </Badge>
              <Paragraph variant={ `secondary` }>
                { `${emailedPdf.fileName} · received ${DateTime.fromISO(emailedPdf.receivedAt).toRelative()}` }
                { emailedPdf.fromAddress ? ` from ${emailedPdf.fromAddress}` : `` }
              </Paragraph>
              <HelpIcon tooltip={ `This PDF will be sent to the customer instead of the one from ${accountingSystemName}.` } />
            </>
          ) : null }

          { usingAlternativePdf ? (
            <>
              <button
                type={ `button` }
                className={ `ml-auto inline-flex items-center gap-1 text-[0.8125rem] font-medium text-paidniceGreen-600 hover:text-paidniceGreen-700 hover:underline cursor-pointer disabled:cursor-not-allowed disabled:text-gray-400` }
                onClick={ () => fileInputRef.current?.click() }
                disabled={ isUploading }
              >
                { isUploading ? <SmallSpinner /> : <ArrowUpTrayIcon className={ `h-4 w-4` } /> }
                { emailedPdf ? `Replace alternative PDF` : `Attach alternative PDF` }
              </button>

              <input
                ref={ fileInputRef }
                type={ `file` }
                accept={ `application/pdf` }
                className={ `hidden` }
                onChange={ onFileSelected }
              />
            </>
          ) : null }
        </div>
      ) : null }

      <button
        type={ `button` }
        className={ `flex w-full items-center justify-between text-left` }
        aria-expanded={ isExpanded }
        aria-controls={ panelId }
        onClick={ onToggleExpanded }
      >
        <Paragraph className={ `font-semibold text-gray-900` }>
          { `Invoice attachments` }
        </Paragraph>
        <ChevronDownIcon className={ classNames(
          `h-5 w-5 text-gray-500 transition-transform duration-200`,
          isExpanded ? `rotate-180` : ``,
        ) }
        />
      </button>

      { isExpanded ? (
        <div
          id={ panelId }
          className={ `mt-3 space-y-3` }
        >
          { isFetching ? (
            <div className={ `flex items-center gap-2` }>
              <SmallSpinner colored />
              <Paragraph variant={ `secondary` }>
                { `Loading attachments...` }
              </Paragraph>
            </div>
          ) : null }

          { isError ? (
            <Paragraph variant={ `secondary` }>
              { `Could not load attachments.` }
            </Paragraph>
          ) : null }

          { !isFetching && !isError && currentData && currentData.attachments.length === 0 ? (
            <Paragraph variant={ `secondary` }>
              { `No attachments found on this invoice.` }
            </Paragraph>
          ) : null }

          { !isFetching && !isError && currentData && currentData.attachments.length > 0 ? (
            <>
              { currentData.emailedPdfFileName ? (
                <Paragraph variant={ `secondary` }>
                  { `Emailed PDF in use instead of an attachment from ${accountingSystemName}: "${currentData.emailedPdfFileName}"` }
                </Paragraph>
              ) : null }

              { currentData.usesAlternativePdfPrefix ? (
                <>
                  <Paragraph variant={ `secondary` }>
                    { `Alternative PDF prefix: "${currentData.alternativePdfPrefix}"` }
                  </Paragraph>
                  <Paragraph variant={ `secondary` }>
                    { nominatedAlternativeAttachment ?
                      `Nominated alternative PDF: ${nominatedAlternativeAttachment.filename}` :
                      `No attachment currently matches the configured alternative PDF prefix.` }
                  </Paragraph>
                </>
              ) : null }

              <ul className={ `space-y-2` }>
                { currentData.attachments.map((attachment, index) => (
                  <li
                    key={ `${attachment.filename}-${index}` }
                    className={ `rounded-md border border-gray-200 bg-gray-50 px-3 py-2` }
                  >
                    <Paragraph className={ `break-all font-medium text-gray-900` }>
                      { attachment.filename }
                    </Paragraph>
                    <div className={ `mt-1 flex flex-wrap gap-2` }>
                      <span className={ classNames(
                        `inline-flex rounded-full px-2 py-0.5 text-xs font-medium`,
                        attachment.availableOnPortalDownload ?
                          `bg-green-100 text-green-800` :
                          `bg-gray-200 text-gray-700`,
                      ) }
                      >
                        { attachment.availableOnPortalDownload ?
                          `Available to download on portal` :
                          `Not available to download on portal` }
                      </span>
                      { attachment.nominatedAlternativePdf ? (
                        <span className={ `inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800` }>
                          { `Nominated alternative PDF` }
                        </span>
                      ) : null }
                    </div>
                  </li>
                )) }
              </ul>
            </>
          ) : null }
        </div>
      ) : null }
    </div>
  );
}
